import type { DropdownMenuItem } from '@nuxt/ui';
import type { ComponentPublicInstance, Ref } from 'vue';
import type { TabsItem } from '#ui/types';
import { computed, nextTick, watch } from 'vue';
import { get } from '#layers/base/public';
import { getLayoutParent } from '#layers/ui/utils/get-layout-parent';
import { useOverflow } from './use-overflow';

/** A tab that has collapsed into the overflow trigger, in a shape a custom overflow menu (e.g. ListCard) can render directly. */
export interface OverflowTabItem {
	/** The tab's value, keyed as `id` so the item is a ListCard item as-is. */
	id: string | number;
	label: string;
	active: boolean;
	icon?: string;
	disabled?: boolean;
}

interface UseCollapsibleTabsOptions<T extends TabsItem> {
	tabsRef: Ref<ComponentPublicInstance | null>;
	overflowTriggerRef: Ref<HTMLElement | null>;
	listTrailingRef?: Ref<HTMLElement | null>;
	items: () => T[] | undefined;
	modelValue: () => string | number | undefined;
	labelKey: () => string;
	enabled: () => boolean;
	/** Fewest tabs to keep visible; the rest collapse into the overflow trigger. Defaults to 1; pass 0 to allow full collapse. */
	minVisibleItems?: () => number;
	onSelect: (value: string | number) => void;
}

/** Returns layout children after flattening wrappers that do not generate boxes. */
function getLayoutChildren(parent: HTMLElement): HTMLElement[] {
	const layoutChildren: HTMLElement[] = [];

	for (const child of parent.children) {
		const element = child as HTMLElement;
		const display = getComputedStyle(element).display;

		if (display === 'none') continue;

		if (display === 'contents') {
			layoutChildren.push(...getLayoutChildren(element));
		}
		else {
			layoutChildren.push(element);
		}
	}

	return layoutChildren;
}

function sumPx(...values: (string | undefined)[]): number {
	return values.reduce((sum, value) => sum + (Number.parseFloat(value ?? '') || 0), 0);
}

/** Get distance between an element's border box and its content box on the inline axis */
function getInlineInsets(style: CSSStyleDeclaration): number {
	return sumPx(style.paddingLeft, style.paddingRight, style.borderLeftWidth, style.borderRightWidth);
}

/** Returns the width available to the tabs root inside its parent layout context. */
function getAvailableFlexWidth(el: HTMLElement): number {
	const parent = getLayoutParent(el);
	if (!parent) return el.offsetWidth;

	const parentStyle = getComputedStyle(parent);
	const ownStyle = getComputedStyle(el);
	// Negative margins let the root bleed past the parent's content box, so subtracting them widens its share.
	const contentWidth = parent.offsetWidth - getInlineInsets(parentStyle) - sumPx(ownStyle.marginLeft, ownStyle.marginRight);
	const display = parentStyle.display ?? '';
	const flexDirection = parentStyle.flexDirection ?? '';
	const isRowFlexParent = display.includes('flex') && !flexDirection.startsWith('column');

	if (!isRowFlexParent) {
		return Math.max(0, contentWidth);
	}

	const gap = Number.parseFloat(parentStyle.columnGap) || 0;
	const siblings = getLayoutChildren(parent).filter((child) => child !== el);
	const siblingWidth = siblings.reduce((width, child) => width + child.offsetWidth, 0);

	return Math.max(0, contentWidth - siblingWidth - siblings.length * gap);
}

/** Manages overflow state for DTabs — hides items that don't fit and provides dropdown menu items for the hidden ones. */
export function useCollapsibleTabs<T extends TabsItem>(options: UseCollapsibleTabsOptions<T>) {
	const rootEl = computed<HTMLElement | null>(() => options.tabsRef.value?.$el ?? null);
	const listEl = computed<HTMLElement | null>(() => rootEl.value?.querySelector('[data-slot="list"]') ?? null);
	const triggerEl = computed<HTMLElement | null>(() => options.overflowTriggerRef.value ?? null);

	/** Resolves the stable tab value for an item at its original index. */
	function getItemValue(item: T, index: number): string {
		return (item.value ?? String(index)) as string;
	}

	const activeIndex = computed(() => options.items()?.findIndex((item, index) => getItemValue(item, index) === options.modelValue()) ?? -1);

	const { splitIndex, overflowCount, hasOverflow, triggerMaxWidth, recalculate } = useOverflow({
		containerRef: rootEl,
		itemsRef: listEl,
		itemCount: computed(() => options.items()?.length ?? 0),
		itemSelector: '[data-slot="trigger"]',
		triggerRef: triggerEl,
		estimatedTriggerWidth: 40,
		enabled: computed(() => options.enabled()),
		observeRef: computed(() => rootEl.value ? getLayoutParent(rootEl.value) : null),
		getContainerWidth: getTabsWidth,
		minVisibleItems: computed(() => options.minVisibleItems?.() ?? 1),
		priorityIndex: activeIndex,
	});

	// A consumer may hide its list-trailing control while tabs are collapsed. Reserving its last known
	// width keeps the available width independent of that visibility, so the measurement can't oscillate:
	// hiding the control would otherwise free space, un-collapse the tabs, and re-show it in a render loop.
	let lastListTrailingWidth = 0;

	/** Returns the width left for tab triggers once list-trailing content has taken its share. */
	function getTabsWidth(container: HTMLElement): number {
		const listStyle = getComputedStyle(listEl.value ?? container);
		// Triggers sit inside the list's content box, so its padding and border are not theirs to use.
		const availableWidth = Math.max(0, getAvailableFlexWidth(container) - (listEl.value ? getInlineInsets(listStyle) : 0));
		const listTrailing = options.listTrailingRef?.value;
		if (!listTrailing) return availableWidth;

		if (listTrailing.offsetWidth > 0) lastListTrailingWidth = listTrailing.offsetWidth;

		const gap = Number.parseFloat(listStyle.columnGap) || 0;
		return Math.max(0, availableWidth - lastListTrailingWidth - gap);
	}

	// Keep the current tab visible when it would otherwise overflow: sacrifice the last tab in front of it instead.
	// The active tab is measured first (`priorityIndex`), so `splitIndex` already makes room for its own width.
	// Only possible when at least one tab is visible (splitIndex > 0); otherwise the active collapses into the trigger.
	const pinnedActiveIndex = computed(() => {
		const active = activeIndex.value;
		return options.enabled() && active >= splitIndex.value && splitIndex.value > 0 ? active : -1;
	});

	function isHidden(index: number): boolean {
		if (pinnedActiveIndex.value >= 0) {
			return index !== pinnedActiveIndex.value && index >= splitIndex.value - 1;
		}

		return index >= splitIndex.value;
	}

	const itemsWithOverflowUi = computed(() => {
		const items = options.items();
		if (!options.enabled() || !items) return items;

		return items.map((item, index) => ({
			...item,
			...(isHidden(index) ? { disabled: true } : {}),
			ui: {
				...item.ui,
				...(isHidden(index) ? { trigger: `${item.ui?.trigger ?? ''} !absolute !invisible` } : {}),
			},
		})) as T[];
	});

	const overflowEntries = computed(() => {
		const items = options.items();
		if (!options.enabled() || !items) return [] as { item: T; index: number }[];
		return items.map((item, index) => ({ item, index })).filter((entry) => isHidden(entry.index));
	});

	const activeInOverflow = computed(() => {
		if (!hasOverflow.value) return false;
		return overflowEntries.value.some((entry) => getItemValue(entry.item, entry.index) === options.modelValue());
	});

	const activeOverflowItem = computed(() => {
		if (!activeInOverflow.value) return null;
		return overflowEntries.value.find((entry) => getItemValue(entry.item, entry.index) === options.modelValue())?.item ?? null;
	});

	const overflowItems = computed<OverflowTabItem[]>(() => overflowEntries.value.map((entry) => {
		const value = getItemValue(entry.item, entry.index);
		return {
			id: value,
			label: get(entry.item, options.labelKey()) as string,
			icon: entry.item.icon,
			disabled: entry.item.disabled,
			active: value === options.modelValue(),
		};
	}));

	const overflowDropdownItems = computed<DropdownMenuItem[][]>(() => {
		if (overflowItems.value.length === 0) return [];

		return [
			overflowItems.value.map((item) => ({
				label: item.label,
				icon: item.icon,
				disabled: item.disabled,
				type: 'checkbox' as const,
				checked: item.active,
				onUpdateChecked: (checked?: boolean) => {
					if (checked) options.onSelect(item.id);
				},
			})),
		];
	});

	// Re-measure when trigger appears/disappears (guard prevents oscillation at boundary)
	let lastHasOverflow: boolean | null = null;

	watch(hasOverflow, (value) => {
		if (value !== lastHasOverflow) {
			lastHasOverflow = value;
			nextTick(recalculate);
		}
	});

	return {
		itemsWithOverflowUi,
		overflowCount,
		hasOverflow,
		hasVisibleTabs: computed(() => splitIndex.value > 0),
		triggerMaxWidth,
		activeInOverflow,
		activeOverflowItem,
		overflowItems,
		overflowDropdownItems,
	};
}
